import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name || !phone) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('name', name);
      formData.append('company', company);
      formData.append('phone', phone);
      formData.append('message', message);
      if (files.length > 0) formData.append('files', files.map(f => f.name).join(', '));

      const response = await fetch('https://readdy.ai/api/form/d6pridea739gopmlgvs0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail(''); setName(''); setCompany(''); setPhone(''); setMessage(''); setFiles([]);
        setTimeout(() => setSubmitStatus('idle'), 6000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#111318] text-white">

      {/* ── 상단 CTA 배너 ── */}
      <div className="bg-orange-500 px-6 py-5 md:px-20">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full">
              <i className="ri-customer-service-2-line text-white text-xl"></i>
            </div>
            <div>
              <p className="text-white font-bold text-base md:text-lg leading-tight">빠른 견적이 필요하신가요?</p>
              <p className="text-white/80 text-xs mt-0.5">소재·두께·수량을 알려주시면 당일 내 검토 후 회신드립니다</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="tel:031-541-1902"
              className="flex items-center gap-2 bg-white text-orange-500 px-5 py-2.5 rounded-full font-bold text-sm hover:bg-orange-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-base"></i>
              031-541-1902
            </a>
            <a
              href="mailto:nstech01@naver.com"
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line text-base"></i>
              이메일 문의
            </a>
          </div>
        </div>
      </div>

      {/* ── 메인 푸터 본문 ── */}
      <div className="px-6 pt-14 pb-10 md:px-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">

            {/* 브랜드 + 견적 폼 */}
            <div className="md:col-span-5">
              {/* 로고 */}
              <div className="mb-5">
                <div className="inline-block bg-white rounded-xl px-3 py-2 shadow-md">
                  <img
                    src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
                    alt="NS-TECH Logo"
                    className="h-10 w-auto object-contain"
                  />
                </div>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">NS-TECH</h3>
              <p className="text-white/50 text-xs leading-relaxed mb-6 max-w-sm">
                CAD 설계부터 레이저 가공, 벤딩, 용접, 조립까지 전 공정을 아우르는 올인원 판금 가공 전문 제조 기업입니다.
              </p>

              {/* 핵심 수치 */}
              <div className="grid grid-cols-3 gap-3 mb-7">
                {[
                  { value: '20+', label: '업력' },
                  { value: '500+', label: '납품 실적' },
                  { value: '99%', label: '납기 준수율' },
                ].map((stat, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
                    <p className="text-orange-400 font-bold text-lg leading-tight">{stat.value}</p>
                    <p className="text-white/50 text-xs mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* 견적 폼 */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 flex items-center justify-center bg-orange-500 rounded-full">
                    <i className="ri-file-list-3-line text-white text-xs"></i>
                  </div>
                  <span className="text-sm font-semibold text-white">견적 문의 신청</span>
                </div>

                <form onSubmit={handleSubmit} data-readdy-form id="quote-form" className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text" name="name" value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="이름 *" required
                      className="bg-white/8 border border-white/15 rounded-lg px-3 py-2.5 text-white placeholder-white/35 focus:outline-none focus:border-orange-400 transition-colors text-xs w-full"
                    />
                    <input
                      type="tel" name="phone" value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="연락처 *" required
                      className="bg-white/8 border border-white/15 rounded-lg px-3 py-2.5 text-white placeholder-white/35 focus:outline-none focus:border-orange-400 transition-colors text-xs w-full"
                    />
                  </div>
                  <input
                    type="email" name="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="이메일 *" required
                    className="w-full bg-white/8 border border-white/15 rounded-lg px-3 py-2.5 text-white placeholder-white/35 focus:outline-none focus:border-orange-400 transition-colors text-xs"
                  />
                  <input
                    type="text" name="company" value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="회사명"
                    className="w-full bg-white/8 border border-white/15 rounded-lg px-3 py-2.5 text-white placeholder-white/35 focus:outline-none focus:border-orange-400 transition-colors text-xs"
                  />
                  <textarea
                    name="message" value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="문의 내용 (소재, 두께, 수량 등)" maxLength={500} rows={3}
                    className="w-full bg-white/8 border border-white/15 rounded-lg px-3 py-2.5 text-white placeholder-white/35 focus:outline-none focus:border-orange-400 transition-colors text-xs resize-none"
                  />

                  {/* 파일 첨부 */}
                  <label htmlFor="footer-file-upload" className="flex items-center gap-2 cursor-pointer group border border-dashed border-white/20 rounded-lg px-3 py-2.5 hover:border-orange-400/50 transition-colors">
                    <i className="ri-attachment-2 text-white/40 group-hover:text-orange-400 text-sm transition-colors"></i>
                    <span className="text-xs text-white/40 group-hover:text-white/60 transition-colors">도면·사양서 첨부 (PDF, DWG, DXF, JPG)</span>
                    <input id="footer-file-upload" type="file" name="files" multiple
                      accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={handleFileChange} className="hidden" />
                  </label>
                  {files.length > 0 && (
                    <div className="space-y-1.5">
                      {files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-white/8 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <i className="ri-file-line text-orange-400 text-xs flex-shrink-0"></i>
                            <span className="text-xs text-white/70 truncate">{file.name}</span>
                          </div>
                          <button type="button" onClick={() => removeFile(index)} className="ml-2 text-white/30 hover:text-white transition-colors cursor-pointer">
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    type="submit" disabled={isSubmitting}
                    className="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all whitespace-nowrap disabled:opacity-50 cursor-pointer flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <><i className="ri-loader-4-line animate-spin"></i> 전송 중...</>
                    ) : (
                      <><i className="ri-send-plane-line"></i> 견적 문의하기</>
                    )}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-500/15 border border-green-500/30 rounded-xl p-3 space-y-1">
                      <div className="flex items-center gap-2 text-green-400 font-medium text-xs">
                        <i className="ri-checkbox-circle-fill"></i>
                        <span>문의가 성공적으로 접수되었습니다!</span>
                      </div>
                      <p className="text-white/50 text-xs pl-5">담당자가 확인 후 빠른 시일 내에 연락드리겠습니다.</p>
                    </div>
                  )}
                  {submitStatus === 'error' && (
                    <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-xl p-3">
                      <i className="ri-error-warning-line"></i>
                      <span>전송 중 오류가 발생했습니다. 다시 시도해주세요.</span>
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* 링크 + 연락처 */}
            <div className="md:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-8">

              {/* 회사정보 */}
              <div>
                <h4 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
                  <span className="w-1 h-4 bg-orange-500 rounded-full inline-block"></span>
                  회사정보
                </h4>
                <ul className="space-y-3">
                  {[
                    { label: '회사소개', href: '#about' },
                    { label: '생산공정', href: '#process' },
                    { label: '보유장비', href: '#equipment' },
                    { label: '회사연혁', href: '/history' },
                    { label: '조직도', href: '/organization' },
                  ].map((item, i) => (
                    <li key={i}>
                      <a href={item.href} className="text-white/50 hover:text-orange-400 transition-colors text-xs flex items-center gap-1.5 group">
                        <i className="ri-arrow-right-s-line text-white/20 group-hover:text-orange-400 transition-colors"></i>
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* 서비스 */}
              <div>
                <h4 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
                  <span className="w-1 h-4 bg-orange-500 rounded-full inline-block"></span>
                  서비스
                </h4>
                <ul className="space-y-3">
                  {[
                    { label: '레이저 가공', href: '/equipment/capability' },
                    { label: '벤딩 가공', href: '/equipment/capability' },
                    { label: '용접 서비스', href: '/equipment/welding' },
                    { label: '가공 가능 재료', href: '/equipment/materials' },
                    { label: '생산 설비', href: '/equipment/production' },
                  ].map((item, i) => (
                    <li key={i}>
                      <a href={item.href} className="text-white/50 hover:text-orange-400 transition-colors text-xs flex items-center gap-1.5 group">
                        <i className="ri-arrow-right-s-line text-white/20 group-hover:text-orange-400 transition-colors"></i>
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* 연락처 */}
              <div className="col-span-2 md:col-span-1">
                <h4 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
                  <span className="w-1 h-4 bg-orange-500 rounded-full inline-block"></span>
                  연락처
                </h4>
                <ul className="space-y-3">
                  {[
                    { icon: 'ri-building-2-line', text: '(주)엔에스테크', sub: '대표 안영달' },
                    { icon: 'ri-map-pin-2-line', text: '경기도 포천시 소흘읍', sub: '소흘로 11' },
                    { icon: 'ri-phone-line', text: '031-541-1902', href: 'tel:031-541-1902' },
                    { icon: 'ri-printer-line', text: '031-541-1903 (팩스)' },
                    { icon: 'ri-mail-line', text: 'nstech01@naver.com', href: 'mailto:nstech01@naver.com' },
                    { icon: 'ri-time-line', text: 'AM 08:00 ~ PM 18:00' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <div className="w-6 h-6 flex items-center justify-center bg-white/8 rounded-md flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-orange-400 text-xs`}></i>
                      </div>
                      <div>
                        {item.href ? (
                          <a href={item.href} className="text-white/60 hover:text-orange-400 transition-colors text-xs">{item.text}</a>
                        ) : (
                          <p className="text-white/60 text-xs">{item.text}</p>
                        )}
                        {item.sub && <p className="text-white/35 text-xs">{item.sub}</p>}
                      </div>
                    </li>
                  ))}
                </ul>

                {/* 운영시간 배지 */}
                <div className="mt-5 bg-orange-500/10 border border-orange-500/20 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    <span className="text-green-400 text-xs font-semibold">운영 중</span>
                  </div>
                  <p className="text-white/50 text-xs">평일 AM 08:00 ~ PM 18:00</p>
                  <p className="text-white/35 text-xs mt-0.5">토·일·공휴일 휴무</p>
                </div>
              </div>
            </div>
          </div>

          {/* ── 구분선 ── */}
          <div className="mt-12 pt-6 border-t border-white/10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="inline-block bg-white rounded-lg px-2 py-1">
                  <img
                    src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
                    alt="NS-TECH"
                    className="h-5 w-auto object-contain"
                  />
                </div>
                <p className="text-white/35 text-xs">
                  © 2025 NS-TECH (주)엔에스테크 | 사업자번호 231-81-06719
                </p>
              </div>
              <div className="flex items-center gap-4">
                <a href="/privacy" className="text-white/35 hover:text-white/60 transition-colors text-xs whitespace-nowrap">개인정보처리방침</a>
                <span className="text-white/20 text-xs">|</span>
                <a href="#map" className="text-white/35 hover:text-white/60 transition-colors text-xs whitespace-nowrap">오시는 길</a>
                <span className="text-white/20 text-xs">|</span>
                <a href="#quote" className="text-white/35 hover:text-white/60 transition-colors text-xs whitespace-nowrap">견적문의</a>
              </div>
            </div>
          </div>

        </div>
      </div>
    </footer>
  );
}
